import { Box } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import { ampli } from "@/config";
import { useLatestCallback } from "@/hooks/useLatestCallback";
import type { DriverRow, TagDefinition } from "@/types/driver";
import DataTable from "@/ui/DataTable";
import type { DataTableInstance } from "@/ui/dataTableFeatures";
import { COLUMN_LABELS } from "./ColumnManager";
import DriverContextMenu, { driversHaveContextMenuActions } from "./DriverContextMenu";

function NoRowsOverlay() {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        bgcolor: "rgba(63, 63, 70, 0.5)",
        color: "rgb(212, 212, 216)",
      }}
    >
      No Results
    </Box>
  );
}

// Not memoized: the TanStack table instance is a stable ref, so a memo on the stable `table` prop would skip
// re-renders when column sizing/order/visibility/pinning state changes. Rendering with the parent keeps it in sync.
export default function DriverDataGrid({
  table,
  drivers,
  activeMembershipId,
  columnPinning,
  onColumnPinningChange,
  tags,
  onOpenDriver,
  highlightedMembershipId,
  rowSelection,
  onContextMenuOpenChange,
}: {
  table: DataTableInstance<DriverRow>;
  drivers: DriverRow[];
  activeMembershipId: string | null;
  columnPinning?: { left: string[] };
  onColumnPinningChange?: (left: string[]) => void;
  tags: TagDefinition[];
  /** Opens the driver's channel; shared by row clicks and the Enter/O/M keyboard shortcuts. */
  onOpenDriver: (driver: DriverRow) => void;
  /** Membership id of the roving keyboard highlight, styled distinctly from the open/active row. */
  highlightedMembershipId: string | null;
  /** Current checkbox selection, so selected rows are visibly indicated (not just via the checkbox). */
  rowSelection: Record<string, boolean>;
  /** Notifies the parent when the right-click menu opens/closes so it can suspend keyboard shortcuts. */
  onContextMenuOpenChange?: (open: boolean) => void;
}) {
  const [contextMenuPosition, setContextMenuPosition] = useState<{ top: number; left: number } | undefined>();
  const [contextMenuTargets, setContextMenuTargets] = useState<DriverRow[]>([]);

  useEffect(() => {
    onContextMenuOpenChange?.(contextMenuPosition != null);
  }, [contextMenuPosition, onContextMenuOpenChange]);

  const handleRowClick = useCallback((row: DriverRow) => onOpenDriver(row), [onOpenDriver]);

  // One identity for the grid's life: `table` is a new object every render, and a new handler re-renders every row.
  const handleRowContextMenu = useLatestCallback((row: DriverRow, event: React.MouseEvent) => {
    const rowSelection = table.state.rowSelection;
    const targets = rowSelection[row.workspaceMembershipId]
      ? drivers.filter((d) => rowSelection[d.workspaceMembershipId])
      : [row];
    if (!driversHaveContextMenuActions(targets)) return;

    const position = { top: event.clientY - 6, left: event.clientX + 2 };
    setContextMenuTargets(targets);
    setContextMenuPosition(position);
  });

  const handleContextMenuClose = useCallback(() => {
    setContextMenuPosition(undefined);
    setContextMenuTargets([]);
  }, []);

  const resolveColumnName = useCallback(
    (columnId: string): string => {
      if (columnId.startsWith("tag:")) {
        const tagId = columnId.slice(4);
        return tags.find((t) => t.id === tagId)?.label ?? columnId;
      }
      return COLUMN_LABELS[columnId] ?? columnId;
    },
    [tags],
  );

  const handleHideColumn = useCallback(
    (columnId: string) => {
      ampli.hideColumn({ columnName: resolveColumnName(columnId) });
    },
    [resolveColumnName],
  );

  const getRowClassName = useCallback(
    (row: DriverRow) => {
      const classes: string[] = [];
      if (row.workspaceMembershipId === activeMembershipId) classes.push("driver-row-active");
      else if (row.feedPrefId) classes.push("driver-row-notified");
      if (rowSelection[row.workspaceMembershipId]) classes.push("driver-row-selected");
      if (row.workspaceMembershipId === highlightedMembershipId) classes.push("driver-row-highlighted");
      return classes.join(" ");
    },
    [activeMembershipId, highlightedMembershipId, rowSelection],
  );

  const getRowCursor = useCallback((row: DriverRow) => (row.feedId == null ? "default" : "pointer"), []);

  return (
    <>
      <DataTable
        table={table}
        columnPinning={columnPinning}
        onColumnPinningChange={onColumnPinningChange}
        onRowClick={handleRowClick}
        onRowContextMenu={handleRowContextMenu}
        getRowClassName={getRowClassName}
        getRowCursor={getRowCursor}
        scrollToRowId={highlightedMembershipId}
        rowTestId={TestIds.driverView.row}
        noRowsContent={<NoRowsOverlay />}
        pagination={false}
        virtualize
        enableColumnHiding
        onHideColumn={handleHideColumn}
        rowHeight={32}
        headerHeight={32}
        sx={{
          "& .MuiTableContainer-root": {
            border: "none",
          },
          "& .MuiTableRow-root": {
            borderLeft: "4px solid transparent",
          },
          "& .MuiTableCell-root": {
            py: 0.25,
            px: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            // Always-visible, low-contrast column separators spanning header + body. The resize handle brightens
            // this edge on hover (see ColumnResizeHandle).
            borderRight: "1px solid rgba(228, 228, 231, 0.12)",
          },
          // The structural checkbox + message-indicator columns get no separator.
          "& .MuiTableCell-root[data-col-id='_select'], & .MuiTableCell-root[data-col-id='inbox']": {
            borderRight: "none",
          },
          "& .driver-row-active": {
            bgcolor: "rgb(82, 82, 91)",
            "&:hover": {
              bgcolor: "rgb(82, 82, 91)",
            },
          },
          "& .driver-row-notified": {
            borderLeftColor: "var(--color-brand-primary-500)",
          },
          // Selected rows get a brand-tinted background so a keyboard (X / Shift+arrow) or mouse selection is
          // obvious beyond the checkbox. Frozen columns paint their own background, so tint them to match.
          "& .driver-row-selected": {
            bgcolor: "rgba(37, 99, 235, 0.22)",
            "&:hover": { bgcolor: "rgba(37, 99, 235, 0.28)" },
          },
          "& .driver-row-selected .frozen-column": {
            bgcolor: "rgba(37, 99, 235, 0.22)",
          },
          "& .MuiTableRow-root.driver-row-selected:hover .frozen-column": {
            bgcolor: "rgba(37, 99, 235, 0.28)",
          },
          // Roving keyboard highlight — a distinct inset ring so it reads separately from the (background-filled)
          // open/active row and can coexist with it and the notified left-border.
          "& .driver-row-highlighted .MuiTableCell-root": {
            boxShadow: "inset 0 1px 0 var(--color-brand-primary-400), inset 0 -1px 0 var(--color-brand-primary-400)",
          },
          "& .driver-row-highlighted .MuiTableCell-root:first-of-type": {
            boxShadow:
              "inset 1px 0 0 var(--color-brand-primary-400), inset 0 1px 0 var(--color-brand-primary-400), inset 0 -1px 0 var(--color-brand-primary-400)",
          },
          "& .driver-row-highlighted .MuiTableCell-root:last-of-type": {
            boxShadow:
              "inset -1px 0 0 var(--color-brand-primary-400), inset 0 1px 0 var(--color-brand-primary-400), inset 0 -1px 0 var(--color-brand-primary-400)",
          },
          "& .MuiTableHead-root .MuiTableCell-root": {
            bgcolor: "rgb(39, 39, 42)",
          },
          "& .MuiTableBody-root .frozen-column": {
            bgcolor: "rgb(39, 39, 42)",
          },
          "& .MuiTableRow-root:hover:not(.driver-row-active) .frozen-column": {
            bgcolor: "rgb(56, 56, 59)",
          },
          "& .driver-row-active .frozen-column": {
            bgcolor: "rgb(82, 82, 91)",
          },
        }}
      />
      <DriverContextMenu
        anchorPosition={contextMenuPosition}
        drivers={contextMenuTargets}
        onClose={handleContextMenuClose}
      />
    </>
  );
}
